// @flow

import _ from 'lodash';
import { createShallowEqualSelector } from '../selector_factories';

import find from './find';
import type { Find } from './find';
import type { Entity } from './index';
import type { GlobalState } from '../../constants';

type JoinAssociater = Find | string;
type JoinForeignKey = string;

const passEntity = (_state, entity) => entity;

const getFinderForAssociater = (foreign_key: JoinForeignKey, associater: ?JoinAssociater): Find => {
  let associationFinder: Find;
  if (_.isFunction(associater)){ // a custom find has been entered, just use it.
    associationFinder = associater;
  } else if (associater){ // a key has been passed, use a find.
    associationFinder = find(associater);
  } else { // attempt to infer the key from the foreign_key
    // FIXME: this singularizer causes issues with words like "address"
    const singular_associater = _.trimEnd(foreign_key, 's'); // do a naive singularizer, covers most of our has_many joins
    associationFinder = find(singular_associater);
  }

  return associationFinder;
};

const makeEntitiesForForeignKey = (foreign_key: JoinForeignKey, finder: Find) => (state: GlobalState, base_entity: ?Entity) => {
  if (!base_entity) return undefined;
  if (!_.has(base_entity, foreign_key)) throw new Error(`Invalid foreign_key: the given entity has no "${foreign_key}" property.`);

  const associated_id = base_entity[foreign_key];
  const associated_entities = finder(state, associated_id);
  return associated_entities;
};

type MakeExtendEntity = typeof makeExtendEntityDefault;
const makeExtendEntityDefault = (foreign_key) => (base_entity, associated_entities) => {
  if (!base_entity) return undefined; // TODO: don't duplicate this

  // replace the array of ids with the hydrated entity
  return {
    ...base_entity,
    [foreign_key]: associated_entities
  };
};

// There are three layers of functions here.
// "makeJoin" is called once, to allow us to pass a custom makeEntity factory when required.
// It is currently local to this module, exported for testing, but may be useful for generalization in the future. It should generally only be called on app start.
export const makeJoin = (makeExtendEntity: MakeExtendEntity = makeExtendEntityDefault) => {
  // The middle "join", is a factory for a joiner. It is generally called while composing queries.
  // It has an intermediate function to enable lazy evaluation (multiple entity queries)
  // ex: query(..., join('foo'))
  const join = (foreign_key: JoinForeignKey, associater: ?JoinAssociater) => () => {
    const finder = getFinderForAssociater(foreign_key, associater);
    const entitiesForForeignKey = makeEntitiesForForeignKey(foreign_key, finder);
    const extendEntity = makeExtendEntity(foreign_key);

    // The inner one, "joiner", returns actual entities with their associations "hydrated".
    // It is generally called by query when it executes.
    const joiner = createShallowEqualSelector([passEntity, entitiesForForeignKey], extendEntity);
    return joiner;
  };

  return join;
};

export default makeJoin();
